import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageTitle, Pill } from "@/components/CrudPage";
import { ArrowDownLeft, ArrowUpRight, ArrowLeftRight } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const dirMeta = {
  inbound: { icon: ArrowDownLeft, tone: "green", label: "Inbound" },
  outbound: { icon: ArrowUpRight, tone: "blue", label: "Outbound" },
  internal: { icon: ArrowLeftRight, tone: "gray", label: "Internal" },
};
const dispTone = { "ANSWERED": "green", "NO ANSWER": "gold", "BUSY": "blue", "FAILED": "red" };

function fmtDur(s) {
  if (!s) return "0:00";
  const m = Math.floor(s / 60); const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}
function fmtTime(iso) {
  try { return new Date(iso).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function CallLogs() {
  const [rows, setRows] = useState([]);
  const [dir, setDir] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = dir === "all" ? "" : `?direction=${dir}`;
    api.get(`/cdr${q}`).then((r) => { setRows(r.data); setLoading(false); });
  }, [dir]);

  return (
    <div>
      <PageTitle title="Call Logs (CDR)" subtitle="Detailed call records across every trunk, extension and department." />

      <div className="mb-4 w-48">
        <Select value={dir} onValueChange={setDir}>
          <SelectTrigger className="bg-card border-border" data-testid="cdr-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All directions</SelectItem>
            <SelectItem value="inbound">Inbound</SelectItem>
            <SelectItem value="outbound">Outbound</SelectItem>
            <SelectItem value="internal">Internal</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border overflow-hidden bg-card/60">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cdr-table">
            <thead>
              <tr className="border-b border-border bg-secondary/40 text-left text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Direction</th>
                <th className="px-4 py-3 font-semibold">From</th>
                <th className="px-4 py-3 font-semibold">To</th>
                <th className="px-4 py-3 font-semibold">Ext</th>
                <th className="px-4 py-3 font-semibold">Time</th>
                <th className="px-4 py-3 font-semibold">Duration</th>
                <th className="px-4 py-3 font-semibold">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">No call records.</td></tr>}
              {rows.map((r) => {
                const m = dirMeta[r.direction] || dirMeta.internal;
                return (
                  <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/30 transition-colors">
                    <td className="px-4 py-3"><span className="flex items-center gap-2"><m.icon size={15} className="text-muted-foreground" /><Pill tone={m.tone}>{m.label}</Pill></span></td>
                    <td className="px-4 py-3 font-mono text-xs">{r.from_number}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.to_number}</td>
                    <td className="px-4 py-3 font-mono text-[hsl(var(--gold))]">{r.extension}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtTime(r.start_time)}</td>
                    <td className="px-4 py-3 font-mono">{fmtDur(r.billsec)}</td>
                    <td className="px-4 py-3"><Pill tone={dispTone[r.disposition] || "gray"}>{r.disposition}</Pill></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
